import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';

/**
 * Hook สำหรับดึงสถิติของร้านค้า (Orders, Auctions, Buyer Requests, Chats, Video Calls)
 */
export const useStoreStats = () => {
  const { currentUser } = useAuth();
  const { chats } = useChat();
  const [stats, setStats] = useState({
    orders: 0,
    auctions: 0,
    buyerRequests: 0,
    chats: 0,
    videoCalls: 0,
    loading: true
  });
  const [storeId, setStoreId] = useState(null);

  // ดึง storeId จาก user
  useEffect(() => {
    const fetchStoreId = async () => {
      if (!currentUser?.uid) {
        setStoreId(null);
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setStoreId(userData.storeId || null);
        }
      } catch (error) {
        console.error('Error fetching storeId:', error);
        setStoreId(null);
      }
    };

    fetchStoreId();
  }, [currentUser]);

  // ดึง Orders
  useEffect(() => {
    if (!storeId) {
      setStats(prev => ({ ...prev, orders: 0 }));
      return;
    }

    const ordersQuery = query(
      collection(db, 'orders'),
      where('storeId', '==', storeId)
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        setStats(prev => ({ ...prev, orders: snapshot.size }));
      },
      (error) => {
        console.error('Error listening to orders:', error);
        setStats(prev => ({ ...prev, orders: 0 }));
      }
    );

    return () => unsubscribe();
  }, [storeId]);

  // ดึง Auctions
  useEffect(() => {
    if (!currentUser?.uid) {
      setStats(prev => ({ ...prev, auctions: 0 }));
      return;
    }

    const auctionsQuery = query(
      collection(db, 'auctions'),
      where('sellerId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(
      auctionsQuery,
      (snapshot) => {
        setStats(prev => ({ ...prev, auctions: snapshot.size }));
      },
      (error) => {
        console.error('Error listening to auctions:', error);
        setStats(prev => ({ ...prev, auctions: 0 }));
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // ดึง Buyer Requests
  useEffect(() => {
    if (!currentUser?.uid) {
      setStats(prev => ({ ...prev, buyerRequests: 0 }));
      return;
    }

    const buyerRequestsQuery = query(
      collection(db, 'buyerRequests'),
      where('buyerId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(
      buyerRequestsQuery,
      (snapshot) => {
        setStats(prev => ({ ...prev, buyerRequests: snapshot.size }));
      },
      (error) => {
        console.error('Error listening to buyer requests:', error);
        setStats(prev => ({ ...prev, buyerRequests: 0 }));
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // ดึง Chats (ใช้ chats จาก ChatContext)
  useEffect(() => {
    if (!chats || !storeId || !currentUser?.uid) {
      setStats(prev => ({ ...prev, chats: 0 }));
      return;
    }

    // Filter chats ที่เกี่ยวข้องกับ store (เหมือน getStoreChats() ใน StoreManagement)
    const validChats = chats.filter(chat => {
      if (!chat) return false;
      // Check for id or _id
      return chat.id || chat._id;
    });

    // Normalize chat IDs
    const normalizedChats = validChats.map(chat => {
      if (!chat.id && chat._id) {
        return { ...chat, id: chat._id };
      }
      return chat;
    });

    // Filter chats related to this store
    const storeChats = normalizedChats.filter(chat => {
      // Check if chat is related to this store
      const isStoreChat = chat.storeId === storeId;
      const isProductInquiry = chat.type === 'product_inquiry' && 
                                chat.participants && 
                                chat.participants.includes(currentUser.uid);
      
      return isStoreChat || isProductInquiry;
    });

    setStats(prev => ({ ...prev, chats: storeChats.length }));
  }, [chats, storeId, currentUser?.uid]);

  // ดึง Video Call Requests
  useEffect(() => {
    if (!storeId) {
      setStats(prev => ({ ...prev, videoCalls: 0 }));
      return;
    }

    const videoCallRequestsQuery = query(
      collection(db, 'videoCallRequests'),
      where('storeId', '==', storeId)
    );

    const unsubscribe = onSnapshot(
      videoCallRequestsQuery,
      (snapshot) => {
        setStats(prev => ({ ...prev, videoCalls: snapshot.size }));
      },
      (error) => {
        console.error('Error listening to video call requests:', error);
        setStats(prev => ({ ...prev, videoCalls: 0 }));
      }
    );

    return () => unsubscribe();
  }, [storeId]);

  // Set loading to false after initial load
  useEffect(() => {
    if (storeId !== null || !currentUser) {
      setStats(prev => ({ ...prev, loading: false }));
    }
  }, [storeId, currentUser]);

  return stats;
};

